import { useEffect, useState } from 'react';
import type { BackgroundItem } from './types';

export function LandingBackground({
  items,
  interactionsDisabled = false,
}: {
  items: BackgroundItem[];
  interactionsDisabled?: boolean;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((item) => (
        <BackgroundItemView
          key={item.src}
          item={item}
          mounted={mounted}
          disabled={interactionsDisabled}
        />
      ))}
    </div>
  );
}

function BackgroundItemView({
  item,
  mounted,
  disabled,
}: {
  item: BackgroundItem;
  mounted: boolean;
  disabled: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [entered, setEntered] = useState(false);

  const baseTransform = mounted ? item.toTransform : item.fromTransform;
  const hoverTransform = `${item.toTransform} scale(1.08) rotate(6deg)`;
  const isHovered = hovered && !disabled;
  const transform = isHovered ? hoverTransform : baseTransform;

  useEffect(() => {
    if (disabled && hovered) setHovered(false);
  }, [disabled, hovered]);

  useEffect(() => {
    if (!mounted) return;
    // Keep the slow entrance transition in effect until it has actually
    // finished playing, then switch to the snappier hover transition.
    const id = setTimeout(() => setEntered(true), item.delay + 1200);
    return () => clearTimeout(id);
  }, [mounted, item.delay]);

  return (
    <div
      className={`absolute ${item.className} ${disabled ? 'pointer-events-none' : 'pointer-events-auto'}`}
      style={{
        opacity: mounted ? (item.opacity ?? 1) : 0,
        transform,
        filter: isHovered ? 'grayscale(1)' : 'grayscale(0)',
        transition: entered
          ? 'opacity 250ms ease-out, transform 350ms cubic-bezier(0.22, 1, 0.36, 1), filter 250ms ease-out'
          : `opacity 900ms ease-out ${item.delay}ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) ${item.delay}ms, filter 250ms ease-out`,
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <img
        src={item.src}
        alt={item.alt}
        width={item.width}
        height={item.height}
        className="h-auto w-full select-none"
      />
    </div>
  );
}
